import { useEffect, useState, useRef, useMemo } from 'react';
import { fetchWithCorpId } from '../utils/api';
import useServerTable from '../utils/useServerTable';
import useColumnPreferences, { useHiddenFilterReset } from '../utils/useColumnPreferences';
import * as XLSX from 'xlsx';
import Drawer from './Drawer';
import EmployeeForm from './EmployeeForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import ColumnPicker from './ui/ColumnPicker';
import { ColumnCells, ColumnFilterRow, ColumnHeaderRow, StatusBadge } from './ui/TableBits';
import { formatDate } from '../utils/dateUtils';
import { useToast } from './ui/Toast';

// One key per filterable column (issue #144); each maps to a same-named
// query param on GET /employees (backend #131).
const EMPTY_FILTER = { corpEmployeeNo: '', fullName: '', designation: '', status: '', category: '', workAuth: '' };

const STATUS_OPTIONS = ['Active', 'Inactive', 'On Leave'];

function EmployeeTable() {
  const toast = useToast();
  const {
    rows: employees, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload, fetchAll,
  } = useServerTable('/employees', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'corpEmployeeNo', direction: 'asc' } });
  // IDs of employees whose timesheet link email is currently being sent.
  const [sendingLinkIds, setSendingLinkIds] = useState(() => new Set());

  // Drawer state: null when closed, { mode: 'create' } or { mode: 'edit', id } when open
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (emp) => setDrawer({ mode: 'edit', id: emp.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); reload(); };

  const handleDownloadExcel = async () => {
    try {
      const rows = await fetchAll();
      const ws = XLSX.utils.json_to_sheet(rows.map(e => ({
        'Emp #': e.corpEmployeeNo, Name: e.fullName, Designation: e.designation,
        Status: e.status, Category: e.category, 'Work Auth': e.workAuth,
        'Start Date': e.startDate, 'End Date': e.endDate,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Employees');
      XLSX.writeFile(wb, 'employees.xlsx');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const handleSendTimesheetLink = async (emp) => {
    if (sendingLinkIds.has(emp.id)) return;
    setSendingLinkIds(prev => new Set(prev).add(emp.id));
    try {
      await fetchWithCorpId(`/employees/${emp.id}/timesheet-link`, { method: 'POST' });
      toast.success('Timesheet link sent.');
    } catch (e) {
      toast.error(e.message || 'Could not send the link.');
    } finally {
      setSendingLinkIds(prev => { const next = new Set(prev); next.delete(emp.id); return next; });
    }
  };

  const iconBtnStyle = {
    background: 'none',
    border: '1px solid var(--hairline)',
    borderRadius: '6px',
    padding: '5px 8px',
    cursor: 'pointer',
    color: 'var(--muted)',
    display: 'inline-flex',
    alignItems: 'center',
  };

  // Column catalogue in default order (issue #148). Emp # is locked because it
  // is the row's open link; Name is the record's identity; Actions is locked
  // everywhere.
  const columnCatalogue = useMemo(() => [
    {
      key: 'corpEmployeeNo', label: 'Emp #', locked: true,
      filter: { name: 'corpEmployeeNo' },
      cell: emp => (
        <td>
          <button type="button" className="btn-link table-link" onClick={() => openEdit(emp)} title="Open employee">
            {emp.corpEmployeeNo}
          </button>
        </td>
      ),
    },
    {
      key: 'fullName', label: 'Name', locked: true,
      filter: { name: 'fullName' },
      cell: emp => <td style={{ fontWeight: '500', color: 'var(--ink)' }}>{emp.fullName}</td>,
    },
    {
      key: 'designation', label: 'Designation',
      filter: { name: 'designation' },
      cell: emp => <td style={{ color: 'var(--muted)' }}>{emp.designation}</td>,
    },
    {
      key: 'status', label: 'Status',
      filter: { name: 'status', options: STATUS_OPTIONS },
      cell: emp => <td><StatusBadge status={emp.status} /></td>,
    },
    {
      key: 'category', label: 'Category',
      filter: { name: 'category' },
      cell: emp => <td>{emp.category}</td>,
    },
    {
      key: 'workAuth', label: 'Work Auth',
      filter: { name: 'workAuth' },
      cell: emp => <td>{emp.workAuth}</td>,
    },
    {
      key: 'startDate', label: 'Start Date',
      cell: emp => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(emp.startDate)}</td>,
    },
    {
      key: 'endDate', label: 'End Date',
      cell: emp => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(emp.endDate)}</td>,
    },
    {
      key: 'actions', label: 'Actions', locked: true, sortable: false,
      cell: emp => (
        <td>
          <button
            type="button"
            onClick={() => handleSendTimesheetLink(emp)}
            disabled={sendingLinkIds.has(emp.id)}
            title="Email timesheet link"
            aria-label="Email timesheet link"
            style={{ ...iconBtnStyle, opacity: sendingLinkIds.has(emp.id) ? 0.5 : 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </button>
        </td>
      ),
    },
    // sendingLinkIds drives the disabled state, so the cells rebuild with it
  ], [sendingLinkIds]);

  const {
    columns, pickerColumns, toggle, move, step, reset, isDefault, saveError,
  } = useColumnPreferences('employees', columnCatalogue);

  useEffect(() => { if (saveError) toast.error(saveError); }, [saveError, toast]);
  useHiddenFilterReset(columns, setFilter, EMPTY_FILTER);

  return (
    <div className="page-container">
      <PageHeader
        title="Employees"
        subtitle="Manage employee records and assignments"
        actions={
          <>
            <button className="btn-info" onClick={handleDownloadExcel}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
            <button className="btn-primary" onClick={openCreate}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Employee
            </button>
          </>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
        <ColumnPicker
          columns={pickerColumns}
          onToggle={toggle}
          onMove={move}
          onStep={step}
          onReset={reset}
          isDefault={isDefault}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <ColumnHeaderRow columns={columns} sortConfig={sortConfig} onSort={handleSort} />
            <ColumnFilterRow columns={columns} filter={filter} onChange={handleFilterChange} />
          </thead>
          <tbody style={{ opacity: loading && employees.length > 0 ? 0.55 : 1 }}>
            {employees.length === 0 ? (
              <TableState
                colSpan={columns.length}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No employees yet"
                emptyHint="Add your first employee to start tracking records and assignments."
                emptyAction={{ label: 'New Employee', onClick: openCreate }}
              />
            ) : employees.map(emp => (
              <tr key={emp.id}>
                <ColumnCells columns={columns} row={emp} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit employee' : 'New employee'}
        subtitle={drawer?.mode === 'edit' ? 'Update employee identity, role, contacts, and family.' : 'Add an employee in three steps: details, contacts, family.'}
        footer={
          drawer?.mode === 'edit' ? (
            <>
              <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
              <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
                Save changes
              </button>
            </>
          ) : (
            // Create mode is a stepper; the form renders its own step footer,
            // so the drawer only offers Cancel here.
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
          )
        }
      >
        {drawer && (
          <EmployeeForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            employeeId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default EmployeeTable;
